/* ============================================================
 * ADG Banner — shared cross-fade slider behaviour
 * Drives every .adg-banner[data-slider] on the site. No-op for a
 * single slide. Autoplay (data-autoplay/data-interval), arrows,
 * dots, swipe, keyboard, pause on hover/focus/hidden-tab, and
 * prefers-reduced-motion aware. Re-inits in the theme editor.
 * ============================================================ */
(function () {
  function initBanner(banner) {
    if (banner.__adgBannerInit) return;

    var slides = Array.prototype.slice.call(banner.querySelectorAll('.adg-banner__slide'));
    banner.setAttribute('data-count', String(slides.length));

    // Nothing to slide: leave the single slide visible (CSS handles it).
    if (slides.length < 2 || !banner.hasAttribute('data-slider')) return;
    banner.__adgBannerInit = true;

    var dots = Array.prototype.slice.call(banner.querySelectorAll('.adg-banner__dot'));
    var prev = banner.querySelector('.adg-banner__arrow--prev');
    var next = banner.querySelector('.adg-banner__arrow--next');

    var idx = 0;
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-active')) { idx = i; break; }
    }

    /* ---- Lazy slides ----
       A slide is a div with a background-image, and the browser fetches every
       one of them before first paint. At ten full-width slides that is most of
       a homepage's weight spent on nine pictures nobody has seen yet. So the
       section renders the first slide with an inline style and the rest with
       data-bg, and they are promoted as they come up.

       Backwards compatible on purpose: a slide that already has an inline
       style has no data-bg and is simply left alone, so every other banner on
       the site behaves exactly as before. If this script never runs, the
       hidden slides never load — but nothing could have advanced to them
       anyway, so nothing is lost. */
    function reveal(slide) {
      if (!slide) return;
      var bg = slide.getAttribute('data-bg');
      if (!bg) return;
      slide.style.backgroundImage = 'url(' + bg + ')';
      slide.removeAttribute('data-bg');
    }

    function show(i) {
      idx = (i + slides.length) % slides.length;
      // Decode the one after this as well, so the next cross-fade has its
      // image ready rather than fading up to nothing.
      reveal(slides[idx]);
      reveal(slides[(idx + 1) % slides.length]);
      for (var k = 0; k < slides.length; k++) {
        slides[k].classList.toggle('is-active', k === idx);
      }
      for (var d = 0; d < dots.length; d++) {
        dots[d].setAttribute('aria-current', d === idx ? 'true' : 'false');
      }
    }
    show(idx);

    if (prev) prev.addEventListener('click', function () { show(idx - 1); restart(); });
    if (next) next.addEventListener('click', function () { show(idx + 1); restart(); });
    dots.forEach(function (dot, k) {
      dot.addEventListener('click', function () { show(k); restart(); });
    });

    /* ---- Autoplay ---- */
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var autoplay = banner.getAttribute('data-autoplay') === 'true' && !reduce;
    var interval = parseInt(banner.getAttribute('data-interval'), 10);
    if (!interval || interval < 2000) interval = 6000;
    var timer = null;
    var userPaused = false;   // set by the pause button; hover/focus never clear it
    function start() { if (autoplay && !userPaused && !timer) timer = setInterval(function () { show(idx + 1); }, interval); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    /* ---- Pause / play (WCAG 2.2.2) ----
       Hover and focus pausing give a touch user nothing, so an autoplaying
       banner also gets a real button at the end of the dots row (built here,
       so every banner that autoplays has one and none that don't). Visible
       only with the theme's accessibility switch on (adg-banner.css), like the
       other visible adjustments in docs/ACCESSIBILITY-VISUAL-CHANGES.md. Not
       built under reduced motion: nothing moves there, so nothing to pause. */
    var ICON_PAUSE = '<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2 1h2.2v8H2zM5.8 1H8v8H5.8z"/></svg>';
    var ICON_PLAY = '<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.5 1l6.5 4-6.5 4z"/></svg>';
    var pauseBtn = null;
    function paintPause() {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      pauseBtn.innerHTML = userPaused ? ICON_PLAY : ICON_PAUSE;
    }
    if (autoplay) {
      var row = banner.querySelector('.adg-banner__dots');
      if (!row) {
        row = document.createElement('div');
        row.className = 'adg-banner__dots adg-banner__dots--controls-only';
        banner.appendChild(row);
      }
      pauseBtn = document.createElement('button');
      pauseBtn.type = 'button';
      pauseBtn.className = 'adg-banner__pause';
      paintPause();
      row.appendChild(pauseBtn);
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        paintPause();
        // Play is an explicit request: resume now, even though the pointer or
        // focus is still on the banner (both would otherwise hold it).
        if (userPaused) { stop(); } else { restart(); }
      });
    }

    banner.addEventListener('mouseenter', stop);
    banner.addEventListener('mouseleave', start);
    banner.addEventListener('focusin', stop);
    banner.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () { if (document.hidden) { stop(); } else { start(); } });

    /* ---- Swipe ---- */
    var x0 = null;
    banner.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    banner.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) { show(idx + (dx < 0 ? 1 : -1)); restart(); }
      x0 = null;
    }, { passive: true });

    start();
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var list = scope.querySelectorAll('.adg-banner');
    for (var i = 0; i < list.length; i++) initBanner(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor re-renders a section on edit — re-init it.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
